import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import useMobileDevice, {
  ismallMobileDevice,
} from '../../hooks/useMobileDevice';
import {
  setAvailablePointsZero,
  setRelevantToCareerPath,
  setRequestsInComplete,
  setRerenderpermissions,
  setUserRole,
} from '../../../redux/userSlice';
import { getLoginDetailFromSession } from '../../helpers/sessionDetails';
import { postRequest } from '../../services';
import STATUS_CODE from '../../constants/statusCode';
import URL from '../../constants/urls';
import STORAGE_KEY from '../../constants/storageKey';
import LOGIN_TYPE from '../../constants/loginType';
import getCurrentHomeUrl from './navbarComponent.helper';
import styles from './navigationComponent.module.css';
import useWindowSize from '../../hooks/useWindowSize';
import RenderMenu from './RenderMenu';
import RenderMobileView from './RenderMobileView';
import RenderWebView from './RenderWebView';
import { EMPLOYER_LOGIN_ESTATE } from '../../constants/loginStates.constants';
import hasPermission from '../../hooks/hasPermission';
import { PERMISSIONS_ACTIONS } from '../../formatter/permissions.bootstrap';
import { API_URL } from '../../constants/apiUrls';
import useGAEventsTracker from '../../hooks/useGAEventsTracker';
import GA from '../../constants/analytics.constants';
import UI from '../../constants/ui';
import AvailablePoints from '../availablePoints';

function NavbarComponent(props) {
  const { loggedInUserRole, setLoggedInUserRole } = props;
  const dispatch = useDispatch();
  const reRenderPermissionsCode = useSelector(
    (state) => state.user.reRenderPermissionsCode
  );
  const [activeUrl, setActiveUrl] = useState(URL.FIND_JOBS);
  // const [shouldShowDialog, setShouldShowDialog] = useState(false);
  const [shouldOpen, setShouldOpen] = useState(false);
  const notificationCount = useSelector(
    (state) => state.user.userInfo.notificationsCount
  );

  const location = useLocation();
  const navigate = useNavigate();

  const currentHomeUrl = useMemo(
    () => getCurrentHomeUrl(loggedInUserRole),
    [loggedInUserRole]
  );

  const isMobileDevice = useMobileDevice();
  const isSmallDevice = ismallMobileDevice();
  const { windowWidth } = useWindowSize();
  const project = useMemo(
    () => getLoginDetailFromSession()?.project,
    [reRenderPermissionsCode]
  );
  const isApprovalPending = useMemo(
    () =>
      getLoginDetailFromSession()?.estate ===
      EMPLOYER_LOGIN_ESTATE.APPROVAL_PENDING,
    [reRenderPermissionsCode]
  );

  const GALogoutEventsTracker = useGAEventsTracker(GA.EVENT_BUTTON.LOGOUT);

  const handleLogout = () => {
    const role = getLoginDetailFromSession()?.role;
    dispatch(setUserRole(role));
    postRequest(API_URL.LOGOUT)
      .then((res) => {
        if (res.code === STATUS_CODE.SUCCESSFULLY_LOGOUT) {
          // eslint-disable-next-line no-unsafe-optional-chaining
          GALogoutEventsTracker(getLoginDetailFromSession()?.name + GA.LOGOUT);
          localStorage.removeItem(STORAGE_KEY.CANDIDATE_DETAILS);
          localStorage.removeItem(STORAGE_KEY.EMPLOYER_DETAILS);
          localStorage.removeItem(STORAGE_KEY.SESSION_DETAILS);
          localStorage.removeItem(STORAGE_KEY.PRICE_PLAN_DETAILS);
          setLoggedInUserRole();
          if (role === LOGIN_TYPE.CANDIDATE) navigate(URL.HOME);
          else if (role === LOGIN_TYPE.EMPLOYER) {
            navigate(URL.RECRUITERS);
          }

          dispatch(setRequestsInComplete());
          dispatch(setRerenderpermissions());
          dispatch(setAvailablePointsZero());
          setShouldOpen(!shouldOpen);
          window.location.reload(true);
        }
      })
      .catch(() => {});
  };

  const onSelectNavOption = (url) => {
    setShouldOpen(!shouldOpen);
    navigate(url);
    if (getLoginDetailFromSession()?.careerpath) {
      dispatch(setRelevantToCareerPath(true));
    } else {
      dispatch(setRelevantToCareerPath(false));
    }
  };

  const hasUpdateJobPermission = useMemo(
    () => hasPermission(PERMISSIONS_ACTIONS.UPDATE_JOB),
    [reRenderPermissionsCode]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    const { pathname } = location;
    // const firstPathName = getFirstPathName(pathname);
    setActiveUrl(pathname);
  }, [location.pathname]);

  return (
    <>
      <nav
        className={`${styles.header} background-ffffff ${
          loggedInUserRole !== LOGIN_TYPE.ADMIN && 'mb-2'
        }`}
        style={{ position: 'sticky', top: 0, zIndex: 1000 }}
      >
        {isMobileDevice ? (
          <div
            className={`container-fluid  d-flex align-items-center justify-content-between ${
              !loggedInUserRole && styles.mainDiv
            } ${isSmallDevice && 'p-0'}`}
          >
            <RenderMobileView
              loggedInUserRole={loggedInUserRole}
              currentHomeUrl={currentHomeUrl}
              activeUrl={activeUrl}
              notificationCount={notificationCount}
              windowWidth={windowWidth}
              isMobileDevice={isMobileDevice}
              isSmallDevice={isSmallDevice}
              setShouldOpen={setShouldOpen}
            />
          </div>
        ) : (
          <div
            className={`${
              windowWidth > 1200 ? 'container' : 'container-fluid'
            } px-3 d-flex align-items-center justify-content-between ${
              !loggedInUserRole && styles.mainDiv
            }`}
          >
            <RenderWebView
              loggedInUserRole={loggedInUserRole}
              currentHomeUrl={currentHomeUrl}
              activeUrl={activeUrl}
              hasUpdateJobPermission={hasUpdateJobPermission}
              project={project}
              isMobileDevice={isMobileDevice}
            />
          </div>
        )}
      </nav>
      {loggedInUserRole === LOGIN_TYPE.EMPLOYER && (
        <div
          className={`${
            windowWidth > 1200 ? 'container' : 'container-fluid'
          }  `}
        >
          <div className="row">
            <div className="d-flex justify-content-end">
              <div
                className={`${
                  isSmallDevice
                    ? 'justify-content-end flex-column'
                    : isMobileDevice
                    ? 'w-100 justify-content-end'
                    : 'justify-content-between'
                } d-flex `}
              >
                <Box
                  display="flex"
                  className="m-1 centered "
                  alignItems="center"
                  justifyContent={isSmallDevice ? 'flex-end' : 'space-between'}
                >
                  {project && !isApprovalPending && (
                    <div
                      className="p-2 background-F3F3F3 me-2"
                      style={{ borderRadius: 10, cursor: 'pointer' }}
                      onClick={() => {
                        navigate(URL.PROJECT_DETAILS);
                      }}
                    >
                      <span className="me-1  subtitle-1-bold color-1F2830 ">
                        {UI.PROJECT_NAME}:
                      </span>
                      <span className="color-1F2830 ">{project}</span>
                    </div>
                  )}
                </Box>

                <Box
                  display="flex"
                  className="m-1  centered"
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <AvailablePoints />
                </Box>
              </div>
            </div>
          </div>
        </div>
      )}
      <RenderMenu
        shouldOpen={shouldOpen}
        setShouldOpen={setShouldOpen}
        isMobileDevice={isMobileDevice}
        activeUrl={activeUrl}
        loggedInUserRole={loggedInUserRole}
        handleLogout={handleLogout}
        onSelectNavOption={onSelectNavOption}
        hasUpdateJobPermission={hasUpdateJobPermission}
        project={project}
        isApprovalPending={isApprovalPending}
        notificationCount={notificationCount}
        windowWidth={windowWidth}
      />
    </>
  );
}
NavbarComponent.propTypes = {
  setLoggedInUserRole: PropTypes.func,
  loggedInUserRole: PropTypes.number,
};

NavbarComponent.defaultProps = {
  setLoggedInUserRole: noop,
  loggedInUserRole: undefined,
};

// RenderNavBarButtons.propTypes = {
//   loggedInUserRole: PropTypes.number,
//   activeUrl: PropTypes.string,
// };

// RenderNavBarButtons.defaultProps = {
//   loggedInUserRole: undefined,
//   activeUrl: '',
// };
export default NavbarComponent;
